import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GrSteps } from '../components/icons/GrSteps';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  CircleHelp,
  LandPlot,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Sparkles,
  Trash2,
  Undo2,
  XCircle,
} from 'lucide-react';

import { AppToast } from '../components/AppToast';
import { AssetQualityControlModal } from '../components/AssetQualityControlModal';
import {
  PasAnnotationWorkbench,
  type PasAnnotationWorkbenchHandle,
} from '../components/pas/PasAnnotationWorkbench';
import { PasSheetFeedbackBanner } from '../components/pas/PasSheetFeedbackBanner';
import { AssetWorkspaceViews } from '../features/workspace/AssetWorkspaceViews';
import {
  buildAssetWorkspacePath,
  isAssetWorkspaceViewParam,
} from '../features/workspace/assetWorkspaceRouting';
import { useAuth } from '../context/AuthContext';
import { assetService } from '../services/assetService';
import { liveUpdatesService } from '../services/liveUpdatesService';
import type { Asset, AssetWithAIExtractions, KnowledgeParticle } from '../types/domain';
import { getAssetTitle, isAssetExtractionActive } from '../utils/assetDisplay';
import {
  type AssetQualityControlMetadata,
  mergeQualityControlIntoAsset,
  readAssetQualityControl,
  resolveQualityControlStatus,
  shouldAutoRunQualityControl,
} from '../utils/assetQualityControl';
import { formatDateTime, resolveSubjectBadge } from '../utils/assetMedia';
import { isPasAssetType, readPasAnchorFromAssetMetadata } from '../utils/pasAsset';
import { isPasAnswerSheetSuspectedAsset, readSelectModalityAssessmentDisplay } from '../utils/pasAnswerSheetMetadata';
import { rememberAssetFeedFocus } from '../components/assets/AssetFeed';

export function AssetDetailPage() {
  const { assetId = '', workspaceView } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (!assetId) return;
    const params = new URLSearchParams(location.search);
    if (params.get('openStairs') === '1') {
      const regionId = params.get('regionId');
      navigate(buildAssetWorkspacePath(assetId, 'practice', { regionId }), { replace: true });
      return;
    }
    if (workspaceView && !isAssetWorkspaceViewParam(workspaceView)) {
      navigate(buildAssetWorkspacePath(assetId, 'fitted'), { replace: true });
    }
  }, [assetId, location.search, navigate, workspaceView]);

  const [asset, setAsset] = useState<AssetWithAIExtractions | null>(null);
  const [particleCount, setParticleCount] = useState(0);
  const [assetParticles, setAssetParticles] = useState<KnowledgeParticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [processingHint, setProcessingHint] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pasSelectMode, setPasSelectMode] = useState(false);
  const [pasCanUndo, setPasCanUndo] = useState(false);
  const [pasRefreshing, setPasRefreshing] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [qualityControlVisible, setQualityControlVisible] = useState(false);
  const [qualityControlLoading, setQualityControlLoading] = useState(false);
  const [qualityControlLoadingMessage, setQualityControlLoadingMessage] = useState('');
  const [qualityControlRunError, setQualityControlRunError] = useState<string | null>(null);
  const [qualityControlReextracting, setQualityControlReextracting] = useState(false);
  const [qualityControlData, setQualityControlData] = useState<AssetQualityControlMetadata | null>(null);
  const previousParticleCountRef = useRef(0);
  const pasWorkbenchRef = useRef<PasAnnotationWorkbenchHandle>(null);
  const menuRootRef = useRef<HTMLDivElement | null>(null);
  const loadingMenuRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const root = menuRootRef.current ?? loadingMenuRootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const mergeAssetWorkbenchUpdate = useCallback((next: Asset) => {
    setAsset((prev) => {
      if (!prev) return next as AssetWithAIExtractions;
      const prevMeta = prev.metadata && typeof prev.metadata === 'object' && !Array.isArray(prev.metadata)
        ? prev.metadata as Record<string, unknown>
        : {};
      const nextMeta = next.metadata && typeof next.metadata === 'object' && !Array.isArray(next.metadata)
        ? next.metadata as Record<string, unknown>
        : {};
      return {
        ...prev,
        metadata: {
          ...prevMeta,
          pasWorkbench: nextMeta.pasWorkbench ?? prevMeta.pasWorkbench,
        },
      };
    });
  }, []);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const [nextAsset, particles] = await Promise.all([
        assetService.getAssetWithAIExtractions(assetId).catch(() => assetService.getAsset(assetId)),
        assetService.listKnowledgeParticlesByAsset(assetId),
      ]);
      setAsset(nextAsset);
      setAssetParticles(particles);
      setParticleCount(particles.length);
      setQualityControlData(readAssetQualityControl(nextAsset));
      previousParticleCountRef.current = particles.length;
      if (isAssetExtractionActive(nextAsset)) {
        setProcessingHint(true);
        liveUpdatesService.watchExtraction({
          assetId,
          taskId: nextAsset.particle_extraction_lock_task_id,
          lockExpiresAt: nextAsset.particle_extraction_lock_expires_at,
          userId: user?.id,
        });
      } else if (particles.length > 0) {
        setProcessingHint(false);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [assetId, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (assetId) rememberAssetFeedFocus(assetId);
  }, [assetId]);

  const handleBack = useCallback(() => {
    if (assetId) rememberAssetFeedFocus(assetId);
    const from = (location.state as { from?: unknown } | null)?.from;
    if (
      typeof from === 'string'
      && from.startsWith('/')
      && !from.includes(`/assets/${assetId}`)
    ) {
      navigate(from);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  }, [assetId, location.state, navigate]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const unsubscribe = liveUpdatesService.subscribe((event) => {
      if (event.kind !== 'asset_particle_extraction_state_changed' || event.assetId !== assetId) {
        return;
      }

      if (event.status === 'processing') {
        setProcessingHint(true);
        setError(null);
        return;
      }

      if (event.status === 'completed') {
        setProcessingHint(false);
        setError(null);
        void load({ silent: true });
        return;
      }

      if (event.status === 'failed') {
        void (async () => {
          try {
            const particles = await assetService.listKnowledgeParticlesByAsset(assetId);
            setParticleCount(particles.length);
            previousParticleCountRef.current = particles.length;
            setProcessingHint(false);
            if (particles.length === 0) {
              setError(event.error || '知识颗粒提取失败');
            } else {
              setError(null);
            }
            const nextAsset = await assetService.getAssetWithAIExtractions(assetId).catch(() => assetService.getAsset(assetId));
            setAsset(nextAsset);
          } catch {
            setProcessingHint(false);
          }
        })();
      }
    });

    return unsubscribe;
  }, [assetId, load, particleCount, user?.id]);

  const title = useMemo(() => (asset ? getAssetTitle(asset) : '素材详情'), [asset]);
  const subjectBadge = useMemo(() => (asset ? resolveSubjectBadge(asset) : null), [asset]);
  const isPas = isPasAssetType(asset?.type);
  const pasAnchor = useMemo(
    () => readPasAnchorFromAssetMetadata(asset?.metadata as Record<string, unknown> | null | undefined),
    [asset?.metadata],
  );
  const isPasAnswerSheetSuspected = isPasAnswerSheetSuspectedAsset(asset);
  const selectModalityDisplay = useMemo(
    () => readSelectModalityAssessmentDisplay(asset?.metadata),
    [asset?.metadata],
  );
  const processing = useMemo(
    () => processingHint || isAssetExtractionActive(asset),
    [asset, processingHint],
  );
  const qualityControlStatus = useMemo(
    () => resolveQualityControlStatus(qualityControlData, asset),
    [asset, qualityControlData],
  );
  const assetHeadline = asset?.description || asset?.subject || '原始素材';

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = window.setTimeout(() => setToastMessage(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toastMessage]);

  const applyQualityControlState = useCallback((qualityControl: AssetQualityControlMetadata | null) => {
    setQualityControlData(qualityControl);
    if (!qualityControl) return;
    setAsset((current) => (current ? mergeQualityControlIntoAsset(current, qualityControl) as AssetWithAIExtractions : current));
  }, []);

  async function handleOpenQualityControl() {
    if (!asset?.id) return;
    setMenuOpen(false);
    setQualityControlVisible(true);
    const cachedQualityControl = qualityControlData || readAssetQualityControl(asset);
    setQualityControlData(cachedQualityControl);
    setQualityControlRunError(null);

    if (particleCount === 0) {
      setQualityControlLoading(false);
      setQualityControlLoadingMessage('');
      return;
    }

    setQualityControlLoading(true);
    setQualityControlLoadingMessage(
      shouldAutoRunQualityControl(cachedQualityControl, { particleCount })
        ? '正在执行品控检测…'
        : '正在加载品控结果…',
    );
    try {
      const { qualityControl: refreshedQualityControl, rerunError } =
        await assetService.ensureAssetQualityControl(asset.id, cachedQualityControl, { particleCount });
      applyQualityControlState(refreshedQualityControl);
      setQualityControlRunError(
        !refreshedQualityControl?.last_checked_at ? (rerunError ?? null) : null,
      );
    } catch (err) {
      setQualityControlRunError(err instanceof Error ? err.message : '加载品控失败');
    } finally {
      setQualityControlLoading(false);
      setQualityControlLoadingMessage('正在加载品控结果…');
    }
  }

  async function handleQualityControlReextract() {
    if (!asset?.id || qualityControlReextracting) return;
    try {
      setQualityControlReextracting(true);
      const extractionResult = await assetService.qualityControlReextract(asset.id);
      if (
        extractionResult
        && typeof extractionResult === 'object'
        && 'accepted' in extractionResult
        && extractionResult.accepted === true
        && typeof extractionResult.taskId === 'string'
      ) {
        setProcessingHint(true);
        liveUpdatesService.watchExtraction({
          assetId: asset.id,
          taskId: extractionResult.taskId,
          userId: asset.user_id || user?.id,
        });
      } else {
        void handleExtract();
      }
      setQualityControlVisible(false);
      setToastMessage('已触发重新提取');
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : '重新提取失败');
    } finally {
      setQualityControlReextracting(false);
    }
  }

  async function handleExtract(options?: { confirmPasSuspectedExtraction?: boolean }) {
    if (!user?.id) return;

    const previousParticleCount = particleCount;
    previousParticleCountRef.current = previousParticleCount;

    if (processing) {
      setError('当前素材正在提取知识颗粒，请稍候。');
      return;
    }

    setExtracting(true);
    setProcessingHint(true);
    setError(null);
    try {
      const extractionResult = await assetService.triggerKnowledgeExtraction({
        userId: user.id,
        assetId,
        ossId: asset?.oss_id,
        confirmPasSuspectedExtraction: options?.confirmPasSuspectedExtraction === true,
      });

      if (
        extractionResult
        && typeof extractionResult === 'object'
        && 'skippedPasSuspected' in extractionResult
        && extractionResult.skippedPasSuspected === true
      ) {
        setProcessingHint(false);
        setError(
          typeof extractionResult.message === 'string' && extractionResult.message.trim()
            ? extractionResult.message
            : '检测到实战答题纸特征，已跳过自动提取。请通过实战答题纸通道上传。',
        );
        await load();
        return;
      }

      const acceptedAsync = extractionResult
        && typeof extractionResult === 'object'
        && 'accepted' in extractionResult
        && extractionResult.accepted === true;

      if (acceptedAsync && typeof extractionResult.taskId === 'string') {
        const taskId = extractionResult.taskId;
        setAsset((current) => {
          if (!current) return current;
          const metadata = { ...(current.metadata || {}) };
          const previousNested = metadata.particleExtraction && typeof metadata.particleExtraction === 'object' && !Array.isArray(metadata.particleExtraction)
            ? metadata.particleExtraction as Record<string, unknown>
            : {};
          metadata.particleExtractionStatus = 'queued';
          metadata.particleExtractionError = null;
          metadata.particleExtraction = {
            ...previousNested,
            status: 'queued',
            error: null,
            taskId,
            updatedAt: new Date().toISOString(),
          };
          return { ...current, metadata };
        });
        setProcessingHint(true);
        const refreshedAsset = await assetService.getAsset(assetId).catch(() => asset);
        liveUpdatesService.watchExtraction({
          assetId,
          taskId,
          lockExpiresAt: refreshedAsset?.particle_extraction_lock_expires_at ?? asset?.particle_extraction_lock_expires_at,
          userId: user.id,
        });
      } else {
        await load({ silent: true });
        setProcessingHint(false);
      }
    } catch (extractError) {
      const message = extractError instanceof Error ? extractError.message : '提取失败';
      if (message.includes('MATERIAL_EXTRACTING') || message.includes('正在提取')) {
        setProcessingHint(true);
        setError(null);
        void load({ silent: true });
      } else {
        let recovered = { stillExtracting: false, hasParticles: false };
        try {
          const [nextAsset, particles] = await Promise.all([
            assetService.getAssetWithAIExtractions(assetId).catch(() => assetService.getAsset(assetId)),
            assetService.listKnowledgeParticlesByAsset(assetId),
          ]);
          setAsset(nextAsset);
          setParticleCount(particles.length);
          previousParticleCountRef.current = particles.length;
          recovered = {
            stillExtracting: isAssetExtractionActive(nextAsset),
            hasParticles: particles.length > previousParticleCount || particles.length > 0,
          };
        } catch {
          recovered = { stillExtracting: false, hasParticles: false };
        }

        if (recovered.stillExtracting || recovered.hasParticles) {
          setProcessingHint(recovered.stillExtracting);
          setError(null);
        } else {
          setProcessingHint(false);
          setError(message);
        }
      }
    } finally {
      setExtracting(false);
      setMenuOpen(false);
    }
  }

  async function handleDelete() {
    if (!user?.id || !window.confirm('删除后会同时移除该素材关联的知识颗粒，此操作不可撤回。')) {
      return;
    }

    setDeleting(true);
    try {
      await assetService.deleteAssetCascade({ userId: user.id, assetId });
      navigate('/assets');
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '删除失败');
    } finally {
      setDeleting(false);
      setMenuOpen(false);
    }
  }

  if (loading && !asset) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (error && !asset) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-danger/30 bg-danger/5 p-6 text-sm text-danger">
        {error}
        <button type="button" onClick={() => void load()} className="ml-3 underline">重试</button>
      </div>
    );
  }

  if (!asset) {
    return null;
  }

  if (isPas) {
    return (
      <div className="fixed inset-0 z-50 bg-[#F8FAFD]">
        <header className="pointer-events-none absolute inset-x-0 top-0 z-40 px-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <div className="pointer-events-auto flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border/70 bg-canvas/90 px-3 py-2 shadow-card backdrop-blur supports-[backdrop-filter]:bg-canvas/75">
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                aria-label="返回"
                title="返回"
              >
                <ArrowLeft size={18} />
              </button>
              <LandPlot size={18} className="shrink-0 text-primary-strong" strokeWidth={2.2} />
              <div className="min-w-0">
                <h1 className="truncate text-sm font-semibold text-ink sm:text-base">实战标注</h1>
                <p className="truncate text-xs text-muted">{title}</p>
              </div>
            </div>

            <div className="ml-auto flex min-w-0 flex-1 basis-full items-center justify-end gap-2 sm:basis-auto">
              <button
                type="button"
                disabled={!pasCanUndo}
                onClick={() => pasWorkbenchRef.current?.undo()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink transition disabled:opacity-40 sm:px-4 sm:py-2 sm:text-sm"
                title="撤销"
              >
                <Undo2 size={16} />
                <span className="hidden sm:inline">撤销</span>
              </button>
              <button
                type="button"
                disabled={pasRefreshing}
                onClick={() => {
                  if (pasRefreshing) return;
                  setPasRefreshing(true);
                  void (async () => {
                    try {
                      await pasWorkbenchRef.current?.reload();
                      setToastMessage('已刷新标注');
                    } catch {
                      setToastMessage('刷新失败');
                    } finally {
                      setPasRefreshing(false);
                    }
                  })();
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink transition disabled:opacity-40 sm:px-4 sm:py-2 sm:text-sm"
                title="从服务器重新加载标注"
              >
                {pasRefreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                <span className="hidden sm:inline">刷新</span>
              </button>
              <PasSheetFeedbackBanner
                variant="toolbar"
                userId={String(user?.id || asset.user_id || '').trim()}
                kpId={pasAnchor?.kpId}
                sessionId={pasAnchor?.sessionId}
              />
              <button
                type="button"
                onClick={() => setPasSelectMode((value) => !value)}
                className={
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition sm:px-4 sm:py-2 sm:text-sm '
                  + (pasSelectMode
                    ? 'bg-primary text-white'
                    : 'border border-border bg-surface text-ink hover:bg-canvas')
                }
                title={pasSelectMode ? '退出框选（可平移）' : '铺台阶'}
              >
                <GrSteps size={16} />
                <span className="hidden sm:inline">{pasSelectMode ? '框选中' : '铺台阶'}</span>
              </button>
              <div ref={loadingMenuRootRef} className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  className="inline-flex items-center justify-center rounded-lg border border-border p-2 text-muted hover:text-ink"
                  aria-label="更多操作"
                  aria-expanded={menuOpen}
                >
                  <MoreHorizontal size={18} />
                </button>
                {menuOpen ? (
                  <div className="absolute right-0 z-30 mt-2 w-52 overflow-hidden rounded-xl border border-border bg-surface shadow-card">
                    <button
                      type="button"
                      onClick={() => void handleDelete()}
                      disabled={deleting}
                      className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-danger hover:bg-danger/5"
                    >
                      <Trash2 size={16} />
                      删除素材及关联颗粒
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <div className="absolute inset-0 flex flex-col pt-14 sm:pt-16">
          <div className="relative min-h-0 flex-1">
            <PasAnnotationWorkbench
              ref={pasWorkbenchRef}
              asset={asset}
              assetId={assetId}
              selectMode={pasSelectMode}
              onCanUndoChange={setPasCanUndo}
              onToast={setToastMessage}
              onAssetUpdated={mergeAssetWorkbenchUpdate}
            />
          </div>
        </div>

        {toastMessage ? <AppToast message={toastMessage} /> : null}
        {error ? (
          <p className="absolute bottom-16 left-4 right-4 z-40 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 pb-8">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex shrink-0 items-center gap-0.5 rounded-lg px-1.5 py-1 text-sm text-primary hover:bg-primary/5"
          >
            <ChevronLeft size={18} />
            返回
          </button>
          {subjectBadge ? (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow"
              style={{ backgroundColor: subjectBadge.color }}
            >
              {subjectBadge.char}
            </span>
          ) : null}
          <h1 className="max-w-[min(100%,18rem)] truncate text-lg font-semibold text-ink sm:max-w-md">
            {title}
          </h1>
          {assetHeadline
            && assetHeadline !== title
            && assetHeadline !== asset.subject ? (
            <span className="max-w-[12rem] truncate text-sm text-muted">{assetHeadline}</span>
          ) : null}
          <span className="rounded-full bg-surface px-2.5 py-0.5 text-xs text-muted">
            {formatDateTime(asset.created_at)}
          </span>
          {asset.subject ? (
            <span className="rounded-full bg-surface px-2.5 py-0.5 text-xs text-muted">{asset.subject}</span>
          ) : null}
          <span className="rounded-full bg-surface px-2.5 py-0.5 text-xs text-muted">
            {processing ? '⬢ 提取中' : `⬢ ${particleCount}`}
          </span>
          {selectModalityDisplay ? (
            <span
              className="max-w-[14rem] truncate rounded-full bg-surface px-2.5 py-0.5 text-xs text-muted"
              title={
                selectModalityDisplay.detailLines.length
                  ? `模态判定：${selectModalityDisplay.detailLines.join(' · ')}`
                  : selectModalityDisplay.chipLabel
              }
            >
              {selectModalityDisplay.chipLabel}
            </span>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!isPasAnswerSheetSuspected && particleCount === 0 ? (
            <button
              type="button"
              onClick={() => void handleExtract()}
              disabled={extracting || processing}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm text-white disabled:opacity-50 sm:px-4"
            >
              <Sparkles size={16} />
              {extracting ? '提交中…' : processing ? '提取中…' : '提取知识颗粒'}
            </button>
          ) : null}

          <div ref={menuRootRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="inline-flex items-center justify-center rounded-lg border border-border p-2 text-muted hover:text-ink"
              aria-label="更多操作"
              aria-expanded={menuOpen}
            >
              <MoreHorizontal size={18} />
            </button>
            {menuOpen ? (
              <div className="absolute right-0 z-30 mt-2 w-52 overflow-hidden rounded-xl border border-border bg-surface shadow-card">
                {!isPasAnswerSheetSuspected ? (
                  <button
                    type="button"
                    onClick={() => { void handleOpenQualityControl(); }}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm hover:bg-canvas"
                  >
                    {qualityControlStatus === 'passed' ? (
                      <CheckCircle2 size={16} className="text-emerald-600" />
                    ) : qualityControlStatus === 'failed' ? (
                      <XCircle size={16} className="text-danger" />
                    ) : (
                      <CircleHelp size={16} className="text-muted" />
                    )}
                    品控&提取
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleting}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-danger hover:bg-danger/5"
                >
                  <Trash2 size={16} />
                  删除素材及关联颗粒
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isPasAnswerSheetSuspected ? (
        <div className="space-y-3 rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-4 text-sm text-ink">
          <p className="font-semibold text-amber-900">疑似实战答题纸</p>
          <p className="leading-relaxed text-ink/85">
            为了提升您知识管理的质量，请删除此素材，通过实战答题纸通道上传。
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              <Trash2 size={16} />
              {deleting ? '删除中…' : '删除此素材'}
            </button>
            <button
              type="button"
              onClick={() => void handleExtract({ confirmPasSuspectedExtraction: true })}
              disabled={extracting || processing}
              className="inline-flex items-center rounded-lg border border-border bg-surface px-4 py-2 text-sm text-primary disabled:opacity-50"
            >
              {extracting ? '提交中…' : '仍要提取知识颗粒'}
            </button>
          </div>
        </div>
      ) : null}

      {asset.ai_brief ? (
        <p className="rounded-2xl border border-border bg-surface px-4 py-3 text-sm leading-relaxed text-ink/80">
          {asset.ai_brief}
        </p>
      ) : null}

      <AssetWorkspaceViews
        assetId={assetId}
        asset={asset}
        particles={assetParticles}
        onAssetUpdated={mergeAssetWorkbenchUpdate}
        onToast={setToastMessage}
      />

      {toastMessage ? <AppToast message={toastMessage} /> : null}

      <AssetQualityControlModal
        open={qualityControlVisible}
        loading={qualityControlLoading}
        loadingMessage={qualityControlLoadingMessage}
        qualityControl={qualityControlData}
        runErrorMessage={qualityControlRunError}
        reextracting={qualityControlReextracting}
        particleCount={particleCount}
        onClose={() => setQualityControlVisible(false)}
        onReextract={() => { void handleQualityControlReextract(); }}
      />

      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}
